import { useState, useRef, useEffect } from 'react';
import { AuthLayout } from '../layout/AuthLayout.jsx';
import { FormField } from '../components/FormField.jsx';
import { TextInput } from '../components/TextInput.jsx';
import { CodeInput } from '../components/CodeInput.jsx';
import { SubmitButton } from '../components/SubmitButton.jsx';
import { FormError, FormSuccess } from '../components/FormError.jsx';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';

export function VerifyEmailPage({ navigate, emailParam }) {
  const { setAuthenticated } = useAuth();
  const [email, setEmail] = useState(emailParam || '');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const codeRef = useRef(null);

  useEffect(() => {
    if (email) codeRef.current?.focus();
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || code.length !== 6) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const data = await api.verifyEmail({ email: email.trim(), code });
      setAuthenticated(data.user, data.organization);
      navigate('/dashboard');
    } catch (e2) {
      setError(e2);
      if (e2 instanceof ApiError && (e2.code === 'invalid_code' || e2.code === 'expired')) {
        codeRef.current?.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending || !email.trim()) return;
    setError(null);
    setSuccess(null);
    setResending(true);
    try {
      const data = await api.resendVerification({ email: email.trim() });
      setSuccess(data?.message || 'If this email exists, a new code has been sent.');
      codeRef.current?.reset();
    } catch (e2) {
      setError(e2);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout
      title="Verify your email"
      subtitle="Enter the 6-digit code we sent you."
    >
      <form onSubmit={handleSubmit} noValidate>
        <FormError error={error} />
        <FormSuccess message={success} />
        <FormField label="Email" htmlFor="verify-email">
          <TextInput
            id="verify-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </FormField>
        <FormField label="Verification code" htmlFor="verify-code">
          <CodeInput
            ref={codeRef}
            value={code}
            onChange={setCode}
            initialFocus={!!emailParam}
            error={error instanceof ApiError && (error.code === 'invalid_code' || error.code === 'expired')}
          />
        </FormField>
        <SubmitButton loading={submitting} disabled={code.length !== 6}>
          Verify
        </SubmitButton>
      </form>
      <div className="auth-links">
        <button
          type="button"
          className="btn-link"
          onClick={handleResend}
          disabled={resending || !email.trim()}
        >
          {resending ? 'Sending…' : 'Resend code'}
        </button>
        <span className="auth-links-sep">·</span>
        <a
          href="/login"
          onClick={(ev) => { ev.preventDefault(); navigate('/login'); }}
        >Back to log in</a>
      </div>
    </AuthLayout>
  );
}
